/**
 * AI CONTEXT
 * Purpose: read-only standout stories inside a Skill History semester.
 * Same bubble as the profile Standout Moments tab: note, then author and
 * date, keyword tags to the right. Curriculum stays as an extra tag.
 * Used by: StudentSkillHistorySection.
 */
import { uniq } from "lodash"
import { useMemo } from "react"
import styled from "styled-components"

import { CLASS_GROUP_LABELS } from "#constants/classOptions"
import { CurriculumConstructor } from "#customTypes/composedTypes/curriculumTypes"
import {
  EvaluationKeywordConstructor,
  StandoutCategory,
  StandoutMomentConstructor,
} from "#customTypes/composedTypes/standoutTypes"
import { UserConstructor } from "#customTypes/composedTypes/userTypes"
import { Uuid } from "#customTypes/globalTypes"
import { useGetSomeStudents } from "#hooks/jotai/useGetStudents"
import { COLORS } from "#styles/colors"

import StandoutMomentBubble from "./StandoutMomentBubble"
import StandoutMomentContent from "./StandoutMomentContent"
import { standoutAuthorName } from "./studentProfileTabHelpers"

const CATEGORY_COLORS: Record<StandoutCategory, string> = {
  thumbsUp: "#52c41a",
  needsWork: "#f5222d",
  secret: "#722ed1",
}

const SMomentBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const SMomentBlockTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${COLORS.GRAY7};
`

type CommonLookups = {
  keywordsById: Record<Uuid, EvaluationKeywordConstructor>
  curriculaById: Record<Uuid, CurriculumConstructor>
  usersById: Record<Uuid, UserConstructor>
  studentsById: Record<
    string,
    { firstName?: string | null; lastName?: string | null }
  >
}

const MomentItem = ({
  moment,
  keywordsById,
  curriculaById,
  usersById,
  studentsById,
}: { moment: StandoutMomentConstructor } & CommonLookups) => {
  const curriculum = curriculaById[moment.curriculumId]
  const categoryColor = moment.category
    ? CATEGORY_COLORS[moment.category]
    : COLORS.GRAY5
  const keywordTags = (moment.tagIds ?? []).map((tagId) => ({
    id: tagId,
    text: keywordsById[tagId]?.text ?? tagId,
    color: categoryColor,
  }))
  const tags = curriculum
    ? [
        {
          id: `curriculum-${curriculum.id}`,
          text: `${CLASS_GROUP_LABELS[curriculum.group]} · ${curriculum.name}`,
        },
        ...keywordTags,
      ]
    : keywordTags

  return (
    <div
      data-testid="skill-history-moment"
      data-moment-id={moment.id}
      data-category={moment.category}
    >
      <StandoutMomentBubble
        note={moment.note}
        authorName={standoutAuthorName(moment, usersById, studentsById)}
        createdAt={moment.createdAt}
        tags={tags}
      >
        <StandoutMomentContent moment={moment} omitKeys={["note"]} />
      </StandoutMomentBubble>
    </div>
  )
}

const StudentStandoutMomentsList = ({
  semester,
  moments,
  keywordsById,
  curriculaById,
  usersById,
}: {
  semester: string
  moments: StandoutMomentConstructor[]
} & Omit<CommonLookups, "studentsById">) => {
  const assistantIds = useMemo(
    () =>
      uniq(
        moments.flatMap((moment) =>
          moment.assistantId ? [moment.assistantId] : []
        )
      ).sort(),
    [moments]
  )
  const taStudents = useGetSomeStudents(assistantIds)
  const studentsById = useMemo(
    () =>
      Object.fromEntries(taStudents.map((student) => [student.id, student])),
    [taStudents]
  )

  if (moments.length === 0) return null

  return (
    <SMomentBlock data-testid="skill-history-moments" data-semester={semester}>
      <SMomentBlockTitle>Standout moments</SMomentBlockTitle>
      {moments.map((moment) => (
        <MomentItem
          key={moment.id}
          moment={moment}
          keywordsById={keywordsById}
          curriculaById={curriculaById}
          usersById={usersById}
          studentsById={studentsById}
        />
      ))}
    </SMomentBlock>
  )
}

export default StudentStandoutMomentsList
